/**
 * Language selection store with localStorage persistence
 * Handles both transcription language and UI language
 */

import { DEFAULT_LANGUAGE, type Language, SUPPORTED_LANGUAGES } from '@refinevoice/shared';
import { create } from 'zustand';
import i18n from '../i18n';

const TRANSCRIPTION_LANGUAGE_KEY = 'refinevoice_transcription_language';
const UI_LANGUAGE_KEY = 'refinevoice_ui_language';

interface LanguageStore {
  selectedTranscriptionLanguage: Language;
  selectedUILanguage: string;
  setTranscriptionLanguage: (language: Language) => void;
  setUILanguage: (languageCode: string) => void;
  getLanguageCode: () => string;
  getWebSpeechCode: () => string;
  getUILanguage: () => string;
}

// Load initial transcription language from localStorage
const loadInitialTranscriptionLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(TRANSCRIPTION_LANGUAGE_KEY);
    if (stored) {
      const languageCode = JSON.parse(stored);
      const language = SUPPORTED_LANGUAGES.find((lang) => lang.code === languageCode);
      if (language) {
        return language;
      }
    }
  } catch (error) {
    console.error('Failed to load transcription language from localStorage:', error);
  }
  return DEFAULT_LANGUAGE;
};

// Load initial UI language from localStorage
const loadInitialUILanguage = (): string => {
  try {
    const stored = localStorage.getItem(UI_LANGUAGE_KEY);
    if (stored) {
      const languageCode = JSON.parse(stored);
      // Check if this language code is supported in our i18n resources
      if (i18n.options.resources?.[languageCode]) {
        return languageCode;
      }
    }
  } catch (error) {
    console.error('Failed to load UI language from localStorage:', error);
  }
  return 'en'; // Default to English
};

export const useLanguageStore = create<LanguageStore>((set, get) => ({
  selectedTranscriptionLanguage: loadInitialTranscriptionLanguage(),
  selectedUILanguage: loadInitialUILanguage(),

  setTranscriptionLanguage: (language: Language) => {
    set({ selectedTranscriptionLanguage: language });
    try {
      localStorage.setItem(TRANSCRIPTION_LANGUAGE_KEY, JSON.stringify(language.code));
    } catch (error) {
      console.error('Failed to save transcription language to localStorage:', error);
    }
  },

  setUILanguage: (languageCode: string) => {
    set({ selectedUILanguage: languageCode });
    try {
      localStorage.setItem(UI_LANGUAGE_KEY, JSON.stringify(languageCode));
      // Update i18n language
      i18n.changeLanguage(languageCode);
    } catch (error) {
      console.error('Failed to save UI language to localStorage:', error);
    }
  },

  getLanguageCode: () => {
    return get().selectedTranscriptionLanguage.code;
  },

  getWebSpeechCode: () => {
    return get().selectedTranscriptionLanguage.webSpeechCode;
  },

  getUILanguage: () => {
    return get().selectedUILanguage;
  },
}));
